import React, { useEffect, useState } from "react";
import PromotersCard from "./PromotersCard";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";

function PromotersList({ competitorName }) {
  const [promoters, setPromoters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const itemsPerPage = isMobile ? 8 : 18;
  const totalPages = Math.ceil(promoters.length / itemsPerPage);

  const indexOfLastPromoter = currentPage * itemsPerPage;
  const indexOfFirstPromoter = indexOfLastPromoter - itemsPerPage;
  const currentPromoters = promoters.slice(indexOfFirstPromoter, indexOfLastPromoter);

  const nextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  const prevPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const getPageNumbers = () => {
    const maxVisible = 10;
    const pageNumbers = [];
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      let start = Math.max(1, currentPage - 4);
      let end = Math.min(totalPages, start + maxVisible - 1);
      
      if (end === totalPages) {
        start = Math.max(1, end - maxVisible + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pageNumbers.push(i);
      }
    }
    
    return pageNumbers;
  };

  useEffect(() => {
    const fetchPromoters = async () => {
      console.log(`Fetching promoters for competitor name: ${competitorName}`);

      try {
        const token = localStorage.getItem("authToken"); // 🔹 Retrieve JWT token

        const response = await fetch("/.netlify/functions/fetchPromoters", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`, // 🔹 Include token in request
          },
          body: JSON.stringify({ competitorName }),
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch promoters: ${response.statusText}`);
        }

        const data = await response.json();
        console.log('Fetched promoters data:', data);

        // Convert sub counts to numeric values for sorting
        const sortedData = data.sort((a, b) => {
          const getNumericValue = (str) => {
            if (!str) return 0;
            const num = parseFloat(str.replace(/[k]/i, ''));
            return str.toLowerCase().includes('k') ? num * 1000 : num;
          };
          
          return getNumericValue(b.subs) - getNumericValue(a.subs);
        });

        setPromoters(sortedData);
        setCurrentPage(1);
      } catch (err) {
        console.error("Error fetching promoters:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (competitorName) {
      fetchPromoters();
    }
  }, [competitorName]);

  if (loading) {
    return <p className="flex items-center justify-center">Loading promoters...</p>;
  }

  if (error) {
    return <p className="text-red-500 flex items-center justify-center">Error: {error}</p>;
  }

  const buttonStyles = `
    w-10 h-10 
    flex items-center justify-center 
    rounded-lg
    transition-colors
    text-gray-100
    focus:outline-none
  `;

  const activeButtonStyles = `
    hover:bg-neutral-100/20
    active:bg-gray-900
  `;

  const disabledButtonStyles = ``;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {currentPromoters.length > 0 ? (
          currentPromoters.map((promoter, index) => (
            <div key={index} className="flex items-center justify-center">
              <PromotersCard name={promoter.name} link={promoter.link} subs={promoter.subs}/>
            </div>
          ))
        ) : (
          <p className="col-span-full flex items-center justify-center">No promoters found.</p>
        )}
      </div>

      {promoters.length > itemsPerPage && (
        <div className="flex items-center justify-center space-x-2">
          <button
            onClick={prevPage}
            disabled={currentPage === 1}
            className={`${buttonStyles} ${currentPage === 1 ? disabledButtonStyles : activeButtonStyles}`}
            aria-label="Previous page"
          >
            <FontAwesomeIcon icon={faChevronLeft} className="h-4 w-4" />
          </button>

          {getPageNumbers().map(number => (
            <button 
              key={number}
              onClick={() => setCurrentPage(number)}
              className={`${buttonStyles} ${
                number === currentPage 
                  ? 'bg-neutral-100/20' 
                  : activeButtonStyles
              }`}
              aria-label={`Page ${number}`}
              aria-current={number === currentPage ? 'page' : undefined}
            >
              {number}
            </button>
          ))}

          <button
            onClick={nextPage}
            disabled={currentPage === totalPages}
            className={`${buttonStyles} ${currentPage === totalPages ? disabledButtonStyles : activeButtonStyles}`}
            aria-label="Next page"
          >
            <FontAwesomeIcon icon={faChevronRight} className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}

export default PromotersList;
